export type AboutCopy = {
  hero: {
    eyebrow: string
    heading: string
    summary: string
    name: string
    role: string
    note: string
  }
  collage: {
    label: string
    items: Array<{ alt: string; caption: string }>
  }
  biography: {
    eyebrow: string
    heading: string
    paragraphs: string[]
  }
  studio: {
    eyebrow: string
    heading: string
    body: string
    steps: string[]
    servicesLink: string
  }
  principles: {
    eyebrow: string
    items: Array<{ title: string; body: string }>
  }
  final: {
    eyebrow: string
    heading: string
    body: string
    conversation: string
    review: string
  }
}

export const ABOUT_COPY: Record<"hr" | "en", AboutCopy> = {
  hr: {
    hero: {
      eyebrow: "Osnivački vođen studio",
      heading:
        "Ista osoba vodi razgovor, oblikuje rješenje i odgovara za isporuku.",
      summary:
        "Zmile je mali, osnivački vođen studio za web-stranice, automatizaciju, praktične AI sustave i prilagođeni softver. Tvrtke rade izravno s Antonijem Novoselnikom — od prvog poslovnog pitanja do rada sustava u praksi.",
      name: "Antonio Novoselnik",
      role: "Osnivač, Zmile",
      note: "Razgovor · Oblikovanje · Izgradnja · Odgovornost",
    },
    collage: {
      label: "Pet prizora koji oblikuju rad",
      items: [
        {
          alt: "Antonio Novoselnik stoji na otvorenom pred panoramom Chicaga.",
          caption: "Osnivački rad · Odgovornost",
        },
        {
          alt: "Antonio Novoselnik odmara s drugim planinarima na stjenovitom planinskom vrhu pod vedrim nebom.",
          caption: "Planinarenje · Ustrajnost",
        },
        {
          alt: "Antonio Novoselnik i partnerica snimaju zajedničku fotografiju na sunčanoj tropskoj plaži.",
          caption: "Putovanje · Perspektiva",
        },
        {
          alt: "Antonio Novoselnik s partnericom nakon završetka triatlonske utrke, s medaljom i startnim brojem.",
          caption: "Half Ironman · Dosljednost",
        },
        {
          alt: "Antonio Novoselnik i partnerica voze unajmljene bicikle tijekom gradskog putovanja.",
          caption: "Kretanje · Znatiželja",
        },
      ],
    },
    biography: {
      eyebrow: "O Antoniju",
      heading: "Tehnologija treba olakšati stvarni rad.",
      paragraphs: [
        "Ja sam Antonio Novoselnik, a razvoj softvera za mene je puno više od samog posla. Oduvijek me privlačila mogućnost da pomoću tehnologije riješim konkretan problem, ubrzam neki proces ili ljudima olakšam svakodnevni rad.",
        "Najviše uživam kada stvarne, često ponavljajuće i nepotrebno komplicirane procese mogu pretvoriti u jednostavna i učinkovita digitalna rješenja. Upravo me ta povezanost tehnologije sa stvarnim potrebama ljudi i poslovanja i danas najviše motivira u radu.",
        "Posljednje dvije godine velik dio slobodnog vremena posvećujem triatlonu. U listopadu 2025. završio sam svoj prvi Half Ironman, a plivanje, biciklizam i trčanje u međuvremenu su postali moj omiljeni način oslobađanja od stresa, razbistravanja misli i postavljanja novih izazova.",
        "Volim i planinarenje, putovanja te boravak u prirodi. Kada nisam za računalom ili na treningu, najradije provodim vrijeme u svom voćnjaku, brinući se o biljkama. Taj sporiji i praktičniji ritam dobra je protuteža digitalnom svijetu i podsjetnik da se najbolji rezultati najčešće grade postupno, uz strpljenje i dosljednost.",
      ],
    },
    studio: {
      eyebrow: "Kako studio radi",
      heading: "Izravna suradnja održava kontekst i odgovornost na istom mjestu.",
      body:
        "Poslovno ograničenje rijetko stane u urednu kategoriju. Problem koji izgleda kao zastarjela web-stranica može se nastaviti u prodajnom procesu, ručnom unosu ili nepovezanim informacijama iza nje. Osnivački vođen model omogućuje da isti kontekst ostane prisutan dok se rješenje oblikuje i gradi.",
      steps: ["Slušanje", "Oblikovanje", "Izgradnja", "Poboljšavanje"],
      servicesLink: "Pogledajte što gradimo",
    },
    principles: {
      eyebrow: "Načela rada",
      items: [
        {
          title: "Stvarni problem prije tehnologije",
          body:
            "Alat vrijedi samo ako uklanja ograničenje, štedi vrijeme ili ljudima daje korisnije informacije.",
        },
        {
          title: "Najjednostavnije korisno rješenje",
          body:
            "Ne uvodimo AI, integraciju ili prilagođeni sustav kada jasniji i pouzdaniji pristup radi bolje.",
        },
        {
          title: "Postupan, vidljiv napredak",
          body:
            "Rješenje postaje opipljivo rano, a zatim se poboljšava uz stvaran proces i ljude koji ga koriste.",
        },
      ],
    },
    final: {
      eyebrow: "Razgovarajmo",
      heading: "Pokažite gdje digitalni dio poslovanja danas zapinje.",
      body:
        "Možemo početi web-stranicom ili konkretnim procesom iza nje. Razgovor vodi izravno osoba koja će oblikovati sljedeći korak.",
      conversation: "Pogledajte radove",
      review: "Zatražite besplatne 30-minutne konzultacije",
    },
  },
  en: {
    hero: {
      eyebrow: "Founder-led Studio",
      heading:
        "The same person leads the conversation, shapes the solution, and remains accountable for delivery.",
      summary:
        "Zmile is a small Founder-led Studio for websites, automation, practical AI systems, and custom software. Companies work directly with Antonio Novoselnik—from the first business question to the system working in practice.",
      name: "Antonio Novoselnik",
      role: "Founder, Zmile",
      note: "Conversation · Shaping · Building · Accountability",
    },
    collage: {
      label: "Five moments that shape the work",
      items: [
        {
          alt: "Antonio Novoselnik standing outdoors in front of the Chicago skyline.",
          caption: "Founder-led work · Accountability",
        },
        {
          alt: "Antonio Novoselnik resting with other hikers on a rocky mountain summit beneath a clear blue sky.",
          caption: "Hiking · Persistence",
        },
        {
          alt: "Antonio Novoselnik and his partner taking a photo together on a sunny tropical beach.",
          caption: "Travel · Perspective",
        },
        {
          alt: "Antonio Novoselnik with his partner after finishing a triathlon, wearing his medal and race number.",
          caption: "Half Ironman · Consistency",
        },
        {
          alt: "Antonio Novoselnik and his partner riding rented bicycles during a city trip.",
          caption: "Movement · Curiosity",
        },
      ],
    },
    biography: {
      eyebrow: "About Antonio",
      heading: "Technology should make real work easier.",
      paragraphs: [
        "I’m Antonio Novoselnik, a software developer driven by a simple belief: technology should make real work easier.",
        "Software development is both my profession and my passion. What first attracted me to it, and what continues to motivate me today, is the opportunity to solve practical problems. I enjoy transforming repetitive, time-consuming workflows into reliable digital solutions that simplify processes, save time, and help people and businesses work more effectively.",
        "Outside of work, I find balance through movement and time outdoors. Over the past two years, I’ve become passionate about triathlon, completing my first Half Ironman in October 2025. Swimming, cycling, and running have since become my favourite ways to clear my head, relieve stress, and challenge myself.",
        "I also enjoy hiking, travelling, and spending time in my orchard. Caring for plants gives me a slower, more hands-on contrast to the digital world and reminds me that the best results, in technology and in life, often come from patience, consistency, and steady progress.",
      ],
    },
    studio: {
      eyebrow: "How the studio works",
      heading: "Direct collaboration keeps context and accountability in the same place.",
      body:
        "A business constraint rarely fits one neat category. What looks like an outdated website can continue into the sales process, manual entry, or disconnected information behind it. The Founder-led model keeps the same context present while the solution is shaped and built.",
      steps: ["Listening", "Shaping", "Building", "Improving"],
      servicesLink: "See what we build",
    },
    principles: {
      eyebrow: "Working principles",
      items: [
        {
          title: "The real problem before the technology",
          body:
            "A tool is useful only when it removes a constraint, saves time, or gives people better information.",
        },
        {
          title: "The simplest useful solution",
          body:
            "We do not introduce AI, an integration, or a custom system when a clearer and more reliable approach works better.",
        },
        {
          title: "Steady, visible progress",
          body:
            "The solution becomes tangible early, then improves alongside the real process and the people using it.",
        },
      ],
    },
    final: {
      eyebrow: "Let’s talk",
      heading: "Show where the digital side of the business gets stuck today.",
      body:
        "We can start with the website or with a specific process behind it. The conversation is led directly by the person who will shape the next step.",
      conversation: "See the work",
      review: "Request a free 30-minute consultation",
    },
  },
}
